import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchAnalytics, analyticsCsvUrl } from "@/lib/api";
import { formatFollowers } from "@/lib/format";

const COLORS = ["#3b82f6", "#22d3ee", "#a78bfa", "#34d399", "#f59e0b"];

const downloadCsv = async () => {
  try {
    const res = await fetch(analyticsCsvUrl, { credentials: "include" });
    if (!res.ok) throw new Error("download failed");
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bagfluence-campaigns.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  } catch (_e) {
    toast.error("Could not download CSV");
  }
};

const Analytics = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchAnalytics().then(setData).catch(() => setData(false));
  }, []);

  if (data === null) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (data === false) {
    return <div className="text-zinc-400">Failed to load analytics.</div>;
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="text-sm text-zinc-500 mt-1">Live performance across revenue, creators, leads, and platforms.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-white/10 bg-white/[0.02] text-zinc-200 hover:bg-white/5" onClick={() => toast.success("Use browser Print → Save as PDF for now")} data-testid="export-pdf-btn">
            <FileText className="h-4 w-4 mr-1.5" /> Export PDF
          </Button>
          <Button onClick={downloadCsv} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <h2 className="text-base font-semibold tracking-tight">Revenue (Lakhs)</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Billed vs collected · last 6 months</p>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.revenueMonths}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0c", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#e4e4e7" }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: "#a1a1aa" }} />
                <Bar dataKey="billed" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                <Bar dataKey="collected" fill="#22d3ee" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <h2 className="text-base font-semibold tracking-tight">Platform Spend</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Live · derived from campaign budgets</p>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.platformBreakdown} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={3}>
                  {data.platformBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0a0a0c", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <h2 className="text-base font-semibold tracking-tight">Creator Leaderboard</h2>
          <div className="mt-4 space-y-2.5">
            {data.leaderboard.map((c, i) => (
              <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.02]">
                <span className="w-6 text-xs text-zinc-500 font-mono">{String(i + 1).padStart(2, "0")}</span>
                <img src={c.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-100 truncate">{c.name}</div>
                  <div className="text-[11px] text-zinc-500">{formatFollowers(c.followers)} · {c.primaryPlatform}</div>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300">{c.er}% ER</span>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-white/8 bg-white/[0.02] p-5">
          <h2 className="text-base font-semibold tracking-tight">Lead Funnel</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Conversion across stages</p>
          <div className="mt-4 space-y-2">
            {data.funnel.map((f, i) => {
              const max = data.funnel[0].count || 1;
              const pct = Math.max(8, (f.count / max) * 100);
              return (
                <div key={f.stage} className="flex items-center gap-3">
                  <span className="w-32 text-xs text-zinc-400">{f.stage}</span>
                  <div className="flex-1 h-7 rounded-md bg-zinc-900 border border-white/5 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-600 to-cyan-500 flex items-center justify-end pr-2 text-xs text-white font-medium"
                      style={{ width: `${pct}%`, opacity: 1 - i * 0.07 }}
                    >
                      {f.count}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Analytics;
